import ApiService from '@/core/services/ApiService';
import { Actions, Mutations } from '@/store/enums/StoreEnums';
import { Module, Action, Mutation, VuexModule } from 'vuex-module-decorators';

export interface AuditLogObject {
  id: number;
  entityType: string;
  entityId: number;
  action: string;
  userId?: number;
  userEmail?: string;
  userName?: string;
  oldValues?: Record<string, any>;
  newValues?: Record<string, any>;
  changedFields: string[];
  ipAddress?: string;
  userAgent?: string;
  createdAt: Date;
  metadata?: Record<string, any>;
}

export interface AuditLogListInfo {
  totalItems: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  auditLogs: Array<AuditLogObject>;
  errors: unknown;
}

export interface AuditLogFilterInfo {
  entityType?: string;
  entityId?: number;
  action?: string;
  userId?: number;
  dateFrom?: string;
  dateTo?: string;
  areaId?: number;
}

@Module
export default class AuditLogsModule
  extends VuexModule
  implements AuditLogListInfo
{
  auditLogs = [] as Array<AuditLogObject>;
  auditLogsFilter = {} as AuditLogFilterInfo;
  allEntityTypes = [] as Array<string>;
  totalItems = 0;
  currentPage = 1;
  pageSize = 10;
  totalPages = 0;
  errors = {};

  /**
   * Get audit logs list
   * @returns Array<AuditLogObject>
   */
  get auditLogsList(): Array<AuditLogObject> {
    return this.auditLogs;
  }

  /**
   * Get total items
   * @returns number
   */
  get auditLogsListCount(): number {
    return this.totalItems;
  }

  /**
   * Get current filter
   * @returns AuditLogFilterInfo
   */
  get getAuditLogsFilter(): AuditLogFilterInfo {
    return this.auditLogsFilter;
  }

  /**
   * Get total pages
   * @returns number
   */
  get auditLogsTotalPages(): number {
    return this.totalPages;
  }

  get getAllEntityTypes(): Array<string> {
    return this.allEntityTypes;
  }

  @Mutation
  [Mutations.SET_AUDIT_LOGS](data) {
    console.log('raw data is: ', data.total);
    this.auditLogs = data.data;
    this.totalItems = data.total;
    this.currentPage = data.page;
    this.pageSize = data.limit;
    this.totalPages = data.totalPages;
    this.errors = {};
  }

  @Mutation
  [Mutations.SET_AUDIT_LOGS_FILTER](obj) {
    this.auditLogsFilter.entityType = obj.entityType;
    this.auditLogsFilter.entityId = obj.entityId;
    this.auditLogsFilter.action = obj.action;
    this.auditLogsFilter.userId = obj.userId;
    this.auditLogsFilter.dateFrom = obj.dateFrom;
    this.auditLogsFilter.dateTo = obj.dateTo;
    this.auditLogsFilter.areaId = obj.areaId;
  }

  @Mutation
  [Mutations.SET_ALL_ENTITY_TYPES](data) {
    this.allEntityTypes = data.data;
  }

  @Mutation
  [Mutations.SET_AUDIT_LOGS_CURRENT_PAGE](page: number) {
    this.currentPage = page;
  }

  @Mutation
  [Mutations.RESET_AUDIT_LOGS_STORE]() {
    this.auditLogs = [] as Array<AuditLogObject>;
    this.auditLogsFilter = {} as AuditLogFilterInfo;
    this.allEntityTypes = [] as Array<string>;
    this.totalItems = 0;
    this.currentPage = 1;
    this.pageSize = 10;
    this.totalPages = 0;
    this.errors = {};
  }

  @Action
  [Actions.UPDATE_AUDIT_LOGS_CURRENT_PAGE](val) {
    this.context.commit(Mutations.SET_AUDIT_LOGS_CURRENT_PAGE, val);
  }

  @Action
  [Actions.UPDATE_AUDIT_LOGS_FILTER](val) {
    this.context.commit(Mutations.SET_AUDIT_LOGS_FILTER, val);
  }

  @Action
  [Actions.GET_AUDIT_LOG](id: number) {
    ApiService.setAuthorizationHeader();
    // return ApiService.get('/audit', id);
  }

  @Action
  [Actions.GET_ALL_ENTITY_TYPES]() {
    ApiService.setAuthorizationHeader();
    return ApiService.get('/audit/entity-types').then(({ data }) => {
      this.context.commit(Mutations.SET_ALL_ENTITY_TYPES, data);
      return data.data;
    });
  }

  @Action
  [Actions.GET_AUDIT_LOGS](options: { limit?: number } = { limit: 10 }) {
    ApiService.setAuthorizationHeader();
    const params = {
      page: this.currentPage,
      limit: options.limit,
      entityType: this.auditLogsFilter.entityType,
      entityId: this.auditLogsFilter.entityId,
      action: this.auditLogsFilter.action,
      userId: this.auditLogsFilter.userId,
      dateFrom: this.auditLogsFilter.dateFrom,
      dateTo: this.auditLogsFilter.dateTo,
      areaId: this.auditLogsFilter.areaId,
    };
    return ApiService.query('/audit', { params })
      .then(({ data }) => {
        this.context.commit(Mutations.SET_AUDIT_LOGS, data.data);
        return data.data.data;
      })
      .catch(({ response }) => {
        console.error(response);
        // this.context.commit(Mutations.SET_ERROR, {
        //   [response.statusText]: [response.data.message],
        // });
      });
  }

  @Action
  [Actions.EXPORT_AUDIT_LOGS]() {
    ApiService.setAuthorizationHeader();
    const params = {
      entityType: this.auditLogsFilter.entityType,
      entityId: this.auditLogsFilter.entityId,
      action: this.auditLogsFilter.action,
      userId: this.auditLogsFilter.userId,
      dateFrom: this.auditLogsFilter.dateFrom,
      dateTo: this.auditLogsFilter.dateTo,
      areaId: this.auditLogsFilter.areaId,
    };

    return ApiService.query('/audit/export', {
      params,
      responseType: 'blob',
    })
      .then(({ data }) => {
        // Create a download link for the exported file
        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
          'download',
          `audit-logs-${new Date().toISOString()}.csv`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        return data;
      })
      .catch(({ response }) => {
        console.error('Error exporting audit logs:', response);
        this.context.commit(Mutations.SET_ERROR, {
          [response?.statusText || 'Error']: [
            response?.data?.message || 'Failed to export audit logs',
          ],
        });
        throw response;
      });
  }
}
